import React, { useState, type ReactNode } from 'react';
import {
  View,
  Text,
  Pressable,
  TextInput,
  ScrollView,
  Modal,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
  type ViewStyle,
  type TextStyle,
} from 'react-native';
import { type Profile } from '@ventureroom/core';
import { useApp, useTheme, type Destination } from './provider';
export function Copy({
  children,
  size = 15,
  muted = false,
  bold = false,
  color,
  style,
  lines,
}: {
  children: ReactNode;
  size?: number;
  muted?: boolean;
  bold?: boolean;
  color?: string;
  style?: TextStyle;
  lines?: number;
}) {
  const t = useTheme();
  return (
    <Text
      numberOfLines={lines}
      style={[
        {
          fontSize: size,
          lineHeight: Math.round(size * 1.5),
          color: color ?? (muted ? t.muted : t.text),
          fontWeight: bold ? '600' : '400',
          fontFamily:
            Platform.OS === 'web' ? 'Inter, ui-sans-serif, system-ui, sans-serif' : undefined,
        },
        style,
      ]}
    >
      {children}
    </Text>
  );
}
export function Heading({ children, size = 28 }: { children: ReactNode; size?: number }) {
  const t = useTheme();
  return (
    <Text
      accessibilityRole="header"
      style={{
        fontSize: size,
        lineHeight: size * 1.16,
        fontWeight: '700',
        letterSpacing: -size * 0.035,
        color: t.text,
      }}
    >
      {children}
    </Text>
  );
}
export function Row({
  children,
  wrap = false,
  between = false,
  style,
}: {
  children: ReactNode;
  wrap?: boolean;
  between?: boolean;
  style?: ViewStyle;
}) {
  return (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          flexWrap: wrap ? 'wrap' : 'nowrap',
          justifyContent: between ? 'space-between' : undefined,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
}
export function Column({
  children,
  gap = 16,
  style,
}: {
  children: ReactNode;
  gap?: number;
  style?: ViewStyle;
}) {
  return <View style={[{ gap }, style]}>{children}</View>;
}
export function Card({
  children,
  style,
  onPress,
  label,
}: {
  children: ReactNode;
  style?: ViewStyle;
  onPress?: () => void;
  label?: string;
}) {
  const t = useTheme();
  const base: ViewStyle = {
    backgroundColor: 'transparent',
    borderBottomWidth: 1,
    borderColor: t.border,
    paddingVertical: 20,
    gap: 12,
  };
  return onPress ? (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => [base, { opacity: pressed ? 0.82 : 1 }, style]}
    >
      {children}
    </Pressable>
  ) : (
    <View style={[base, style]}>{children}</View>
  );
}
export function Button({
  children,
  onPress,
  kind = 'primary',
  disabled = false,
  compact = false,
  label,
  color,
}: {
  children: ReactNode;
  onPress: () => void;
  kind?: 'primary' | 'secondary' | 'quiet' | 'danger';
  disabled?: boolean;
  compact?: boolean;
  label?: string;
  color?: string;
}) {
  const t = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => ({
        minHeight: 44,
        paddingHorizontal: compact ? 14 : 22,
        paddingVertical: 11,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor:
          kind === 'primary'
            ? color || t.accent
            : kind === 'secondary'
              ? 'transparent'
              : kind === 'danger'
                ? 'transparent'
                : 'transparent',
        borderWidth: 0,
        borderColor: t.border,
        opacity: disabled ? 0.45 : pressed ? 0.75 : 1,
      })}
    >
      <Text
        style={{
          fontWeight: '600',
          fontSize: 14,
          color: color && kind !== 'primary' ? color : kind === 'primary' ? t.onAccent : kind === 'danger' ? t.danger : t.text,
        }}
      >
        {children}
      </Text>
    </Pressable>
  );
}
export function Tag({ children, accent = false }: { children: ReactNode; accent?: boolean }) {
  const t = useTheme();
  return (
    <View
      style={{
        paddingVertical: 2,
        alignSelf: 'flex-start',
      }}
    >
      <Copy size={11} color={accent ? t.accent : t.muted} bold>
        {children}
      </Copy>
    </View>
  );
}
export function Avatar({
  person,
  size = 56,
}: {
  person: Pick<Profile, 'name' | 'avatar'>;
  size?: number;
}) {
  const t = useTheme();
  return (
    <View
      accessibilityLabel={`${person.name} avatar`}
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.34,
        backgroundColor: t.avatars[person.avatar],
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <View
        style={{
          position: 'absolute',
          width: size * 0.8,
          height: size * 0.8,
          borderRadius: 100,
          borderWidth: size * 0.15,
          borderColor: t.surface,
          opacity: 0.2,
          right: -size * 0.3,
          bottom: -size * 0.3,
        }}
      />
      <Text style={{ fontSize: size * 0.33, fontWeight: '600', color: t.text }}>
        {person.name
          .split(' ')
          .map((s) => s[0])
          .slice(0, 2)
          .join('')}
      </Text>
    </View>
  );
}
export function Brand({ small = false }: { small?: boolean }) {
  const t = useTheme();
  return (
    <Row>
      <View
        style={{
          width: small ? 30 : 38,
          height: small ? 30 : 38,
          borderRadius: 11,
          backgroundColor: t.accent,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={{ color: t.onAccent, fontWeight: '800', fontSize: 23 }}>v.</Text>
      </View>
      <Text
        style={{ fontSize: small ? 20 : 24, fontWeight: '700', letterSpacing: -0.8, color: t.text }}
      >
        VentureRoom
      </Text>
    </Row>
  );
}
export function Empty({
  title,
  detail,
  action,
}: {
  title: string;
  detail: string;
  action?: ReactNode;
}) {
  const t = useTheme();
  return (
    <Card style={{ paddingVertical: 40, alignItems: 'center' }}>
      <View
        style={{
          height: 64,
          width: 64,
          borderRadius: 24,
          backgroundColor: t.hero,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Copy size={28}>↗</Copy>
      </View>
      <Heading size={20}>{title}</Heading>
      <Copy muted style={{ maxWidth: 380 }}>
        {detail}
      </Copy>
      {action}
    </Card>
  );
}
export function Notice() {
  const { notice, error, refresh } = useApp();
  const t = useTheme();
  if (!notice && !error) return null;
  return (
    <View
      accessibilityRole="alert"
      accessibilityLiveRegion="polite"
      style={{
        paddingVertical: 6,
        gap: 8,
      }}
    >
      <Copy color={error ? t.danger : t.success}>{error || notice}</Copy>
      {!!error && (
        <Button kind="quiet" compact onPress={() => void refresh()}>
          Refresh workspace
        </Button>
      )}
    </View>
  );
}
export function Page({
  title,
  action,
  back,
  scroll = true,
  children,
}: {
  title: string;
  action?: ReactNode;
  back?: Destination | (() => void);
  scroll?: boolean;
  children: ReactNode;
}) {
  const t = useTheme();
  const { goBack } = useApp();
  const { width, height } = useWindowDimensions();
  const padding = width < 600 ? 20 : 36;
  const bodyStyle: ViewStyle = {
    paddingHorizontal: padding,
    paddingTop: 8,
    paddingBottom: scroll ? 36 : 12,
    width: '100%',
    maxWidth: 1250,
    alignSelf: 'center',
    gap: scroll ? 20 : 12,
    ...(!scroll ? { flex: 1, minHeight: 0 } : {}),
  };
  return (
    <View
      style={{
        flex: 1,
        backgroundColor: t.background,
        ...(!scroll && Platform.OS === 'web' ? { height: Math.max(590, height - 140) } : {}),
      }}
    >
      {!!(title || back || action) && (
        <View
          style={{
            paddingHorizontal: padding,
            paddingTop: 16,
            paddingBottom: 12,
            width: '100%',
            maxWidth: 1250,
            alignSelf: 'center',
          }}
        >
          <Row between>
            {back && (
              <Button
                compact
                kind="quiet"
                label="Back"
                onPress={() => (typeof back === 'function' ? back() : goBack(back))}
              >
                ←
              </Button>
            )}
            <View style={{ flex: 1 }}>
              <Heading size={width < 600 ? 25 : 30}>{title}</Heading>
            </View>
            {action}
          </Row>
        </View>
      )}
      {scroll ? (
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={bodyStyle}
          keyboardShouldPersistTaps="handled"
        >
          <Notice />
          {children}
        </ScrollView>
      ) : (
        <View style={bodyStyle}>
          <Notice />
          {children}
        </View>
      )}
    </View>
  );
}
export function Loading() {
  const t = useTheme();
  return (
    <View
      style={{
        flex: 1,
        backgroundColor: t.background,
        padding: 40,
        justifyContent: 'center',
        gap: 22,
      }}
    >
      <Brand />
      <ActivityIndicator color={t.accent} />
      <Copy muted>Opening your room…</Copy>
      {[1, 2, 3].map((i) => (
        <View key={i} style={{ height: 70, borderRadius: 20, backgroundColor: t.surfaceAlt }} />
      ))}
    </View>
  );
}
export function Segments({
  items,
  value,
  onChange,
}: {
  items: string[];
  value: string;
  onChange: (s: string) => void;
}) {
  const t = useTheme();
  return (
    <ScrollView
      horizontal
      style={{ flexGrow: 0, flexShrink: 0 }}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        gap: 6,
        paddingVertical: 2,
        alignSelf: 'flex-start',
      }}
    >
      {items.map((item) => (
        <Pressable
          key={item}
          accessibilityRole="tab"
          accessibilityState={{ selected: value === item }}
          onPress={() => onChange(item)}
          style={{
            paddingHorizontal: 18,
            paddingVertical: 12,
            minHeight: 44,
            borderBottomWidth: 2,
            borderBottomColor: value === item ? t.accent : 'transparent',
          }}
        >
          <Copy size={13} bold color={value === item ? t.text : t.muted}>
            {item}
          </Copy>
        </Pressable>
      ))}
    </ScrollView>
  );
}
export function Metric({ value, label }: { value: string | number; label: string }) {
  return (
    <Column gap={3} style={{ flex: 1, minWidth: 95 }}>
      <Heading size={27}>{value}</Heading>
      <Copy size={12} muted>
        {label}
      </Copy>
    </Column>
  );
}
export type Field = {
  key: string;
  label: string;
  type?: 'text' | 'long' | 'number' | 'select' | 'multi' | 'secret';
  options?: string[];
  hint?: string;
  required?: boolean;
};
export type FormValues = Record<string, string | string[] | number>;
export function Input({
  label,
  value,
  onChange,
  onSubmit,
  placeholder,
  multiline = false,
  secret = false,
  numeric = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  multiline?: boolean;
  secret?: boolean;
  numeric?: boolean;
}) {
  const t = useTheme();
  return (
    <Column gap={7}>
      <Copy size={13} bold>
        {label}
      </Copy>
      <TextInput
        accessibilityLabel={label}
        value={value}
        onChangeText={onChange}
        onSubmitEditing={onSubmit}
        onKeyPress={(e) => {
          if (e.nativeEvent.key === 'Enter' && onSubmit) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((e.nativeEvent as any).shiftKey) return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (typeof (e as any).preventDefault === 'function') {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (e as any).preventDefault();
            }
            onSubmit();
          }
        }}
        placeholder={placeholder}
        placeholderTextColor={t.muted}
        multiline={multiline}
        secureTextEntry={secret}
        keyboardType={numeric ? 'decimal-pad' : 'default'}
        autoCapitalize={secret ? 'none' : 'sentences'}
        style={{
          borderWidth: 1,
          borderColor: t.border,
          borderRadius: 12,
          padding: 13,
          fontSize: 15,
          color: t.text,
          backgroundColor: t.surface,
          minHeight: multiline ? 90 : 48,
          textAlignVertical: multiline ? 'top' : 'center',
        }}
      />
    </Column>
  );
}
export function FormFields({
  fields,
  values,
  onChange,
}: {
  fields: Field[];
  values: FormValues;
  onChange: (key: string, value: string | string[] | number) => void;
}) {
  const t = useTheme();
  return (
    <Column gap={20}>
      {fields.map((field) => (
        <Column key={field.key} gap={6}>
          {field.type === 'select' || field.type === 'multi' ? (
            <>
              <Copy size={13} bold>
                {field.label}
              </Copy>
              <Row wrap>
                {field.options?.map((option) => {
                  const value = values[field.key];
                  const selected = Array.isArray(value) ? value.includes(option) : value === option;
                  return (
                    <Pressable
                      key={option}
                      accessibilityRole={field.type === 'multi' ? 'checkbox' : 'radio'}
                      accessibilityLabel={`${field.label}: ${option}`}
                      accessibilityState={
                        field.type === 'multi' ? { checked: selected } : { selected }
                      }
                      onPress={() =>
                        onChange(
                          field.key,
                          field.type === 'multi'
                            ? selected
                              ? (Array.isArray(value) ? value : []).filter((v) => v !== option)
                              : [...(Array.isArray(value) ? value : []), option]
                            : option,
                        )
                      }
                      style={{
                        paddingVertical: 11,
                        paddingHorizontal: 13,
                        borderRadius: 11,
                        borderWidth: 1,
                        borderColor: selected ? t.accent : t.border,
                        backgroundColor: selected ? t.accentSoft : t.surface,
                        minHeight: 44,
                      }}
                    >
                      <Copy size={13} color={selected ? t.accent : t.muted}>
                        {selected ? '✓  ' : ''}
                        {option}
                      </Copy>
                    </Pressable>
                  );
                })}
              </Row>
            </>
          ) : (
            <Input
              label={field.label}
              value={String(values[field.key] ?? '')}
              onChange={(v) =>
                onChange(field.key, field.type === 'number' ? (v === '' ? '' : Number(v)) : v)
              }
              placeholder={field.hint}
              multiline={field.type === 'long'}
              secret={field.type === 'secret'}
              numeric={field.type === 'number'}
            />
          )}
          {!!field.hint && (field.type === 'select' || field.type === 'multi') && (
            <Copy size={12} muted>
              {field.hint}
            </Copy>
          )}
        </Column>
      ))}
    </Column>
  );
}
export function Sheet({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const t = useTheme();
  return (
    <Modal
      transparent
      visible
      animationType={useApp().reduceMotion ? 'none' : 'fade'}
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{
          flex: 1,
          backgroundColor: t.overlay,
          justifyContent: 'center',
          alignItems: 'center',
          padding: 16,
        }}
      >
        <Pressable
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          onPress={onClose}
          accessibilityLabel="Close pop-up"
        />
        <View
          accessibilityViewIsModal
          style={{
            maxHeight: '94%',
            width: '100%',
            maxWidth: 640,
            backgroundColor: t.background,
            borderRadius: 24,
            overflow: 'hidden',
          }}
        >
          <Row between style={{ padding: 20, borderBottomWidth: 1, borderColor: t.border }}>
            <Button kind="quiet" compact label="Back" onPress={onClose}>
              ←
            </Button>
            <View style={{ flex: 1 }}>
              <Heading size={22}>{title}</Heading>
            </View>
          </Row>
          <ScrollView
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ padding: 24, gap: 20 }}
          >
            <Notice />
            {children}
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}
export function FormSheet({
  title,
  fields,
  initial,
  onSave,
  onClose,
  submit = 'Save',
}: {
  title: string;
  fields: Field[];
  initial: FormValues;
  onSave: (v: FormValues) => Promise<boolean>;
  onClose: () => void;
  submit?: string;
}) {
  const [values, setValues] = useState(initial);
  const [validation, setValidation] = useState('');
  const { busy } = useApp();
  const t = useTheme();
  async function save() {
    const missing = fields.find(
      (f) =>
        f.required &&
        (values[f.key] === undefined ||
          values[f.key] === '' ||
          (Array.isArray(values[f.key]) && (values[f.key] as string[]).length === 0)),
    );
    if (missing) {
      setValidation(`Please complete ${missing.label.toLowerCase()}.`);
      return;
    }
    try {
      if (await onSave(values)) onClose();
    } catch (e) {
      setValidation(e instanceof Error ? e.message : 'Please check your entries.');
    }
  }
  return (
    <Sheet title={title} onClose={onClose}>
      <FormFields
        fields={fields}
        values={values}
        onChange={(key, value) => setValues((v) => ({ ...v, [key]: value }))}
      />
      {!!validation && <Copy color={t.danger}>{validation}</Copy>}
      <Button disabled={busy} onPress={() => void save()}>
        {busy ? 'Saving…' : submit}
      </Button>
    </Sheet>
  );
}
export function dateLabel(value: string) {
  return new Date(value.length === 10 ? value + 'T12:00:00' : value).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}
export function timeLabel(value: string) {
  return new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function Disclosure({
  title,
  children,
  open = false,
}: {
  title: string;
  children: ReactNode;
  open?: boolean;
}) {
  const [expanded, setExpanded] = useState(open);
  const t = useTheme();
  return (
    <View style={{ borderBottomWidth: 1, borderColor: t.border, paddingVertical: 12 }}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={title}
        accessibilityState={{ expanded }}
        onPress={() => setExpanded(!expanded)}
        style={{ minHeight: 48, justifyContent: 'center' }}
      >
        <Row between>
          <Heading size={20}>{title}</Heading>
          <Copy muted>{expanded ? '−' : '+'}</Copy>
        </Row>
      </Pressable>
      {expanded && <Column style={{ paddingTop: 16, paddingBottom: 12 }}>{children}</Column>}
    </View>
  );
}
